import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Network } from '@capacitor/network';
import { App } from '@capacitor/app';
import { Storage } from '@ionic/storage-angular';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { OdooSyncService } from './odoo-sync.service';
import { environment } from 'src/environments/environment';

export type TipoOperacion =
  | 'estado_orden'
  | 'cantidades'
  | 'progreso'
  | 'levantamiento'
  | 'recobro'
  | 'cierre_orden'
  | 'visita_accion'
  | 'reporte_visita';

export interface OperacionPendiente {
  id: string;
  tipo: TipoOperacion;
  payload: any;
  // Si se define, una operación nueva con el mismo tipo+clave reemplaza a la anterior (ej. cierre de la misma orden)
  clave?: string;
  creadaEn: string;
  intentos: number;
  // Pasos ya enviados al servidor; evita duplicar registros si una operación de varios pasos falla a la mitad
  pasosHechos: string[];
  ultimoError?: string;
}

const KEY_COLA = 'sync_cola';
const KEY_FALLIDAS = 'sync_fallidas';
const MAX_INTENTOS = 8;
const INTERVALO_REINTENTO_MS = 60_000;

// Colas del esquema anterior, en el orden en que se migran a la cola única
const COLAS_LEGADAS: { key: string; tipo: TipoOperacion; clave?: (p: any) => string }[] = [
  { key: 'estados_pendientes', tipo: 'estado_orden' },
  { key: 'cantidades_pendientes', tipo: 'cantidades' },
  { key: 'progresos_pendientes', tipo: 'progreso' },
  { key: 'levantamientos_pendientes', tipo: 'levantamiento' },
  { key: 'recobros_pendientes', tipo: 'recobro' },
  { key: 'ordenes_pendientes', tipo: 'cierre_orden', clave: p => p.idOrden },
  { key: 'visitas_acciones_pendientes', tipo: 'visita_accion' },
  { key: 'visitas_pendientes', tipo: 'reporte_visita', clave: p => p.idVisita },
];

@Injectable({
  providedIn: 'root'
})
export class SyncService {

  /** Número de operaciones esperando a enviarse (útil para mostrar un badge en la UI). */
  readonly pendientes$ = new BehaviorSubject<number>(0);
  /** Operaciones que el servidor rechazó y requieren revisión manual. */
  readonly fallidas$ = new BehaviorSubject<number>(0);

  private sincronizando = false;
  private idEnCurso: string | null = null;
  private inicializado = false;
  // Serializa toda lectura/escritura de la cola para que encolar y sincronizar no se pisen
  private candado: Promise<unknown> = Promise.resolve();

  constructor(
    private storage: Storage,
    private http: HttpClient,
    private odooSync: OdooSyncService
  ) { }

  async init() {
    if (this.inicializado) return;
    this.inicializado = true;

    await this.migrarColasLegadas();
    await this.actualizarContadores();

    Network.addListener('networkStatusChange', status => {
      console.log('📡 Estado de red cambiado. ¿Hay internet?:', status.connected);
      if (status.connected) this.sincronizar();
    });
    window.addEventListener('online', () => this.sincronizar());
    App.addListener('resume', () => this.sincronizar()).catch(() => { /* web sin plugin App */ });
    setInterval(() => { if (navigator.onLine) this.sincronizar(); }, INTERVALO_REINTENTO_MS);

    this.sincronizar();
  }

  /** Guarda una operación para enviarla cuando haya conexión. */
  async encolar(tipo: TipoOperacion, payload: any, clave?: string) {
    await this.conCandado(async () => {
      let cola = await this.leerCola();
      if (clave) {
        cola = cola.filter(op =>
          !(op.tipo === tipo && op.clave === clave && op.pasosHechos.length === 0 && op.id !== this.idEnCurso)
        );
      }
      cola.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        tipo,
        payload,
        clave,
        creadaEn: new Date().toISOString(),
        intentos: 0,
        pasosHechos: []
      });
      await this.storage.set(KEY_COLA, cola);
    });
    await this.actualizarContadores();

    // navigator.onLine puede decir "offline" con señal intermitente; intentar no cuesta nada
    if (navigator.onLine) this.sincronizar();
  }

  /** Procesa la cola en orden FIFO. Se detiene al primer error de red y reintenta después. */
  async sincronizar() {
    if (this.sincronizando) return;
    if (!localStorage.getItem('token')) return;
    this.sincronizando = true;

    try {
      while (true) {
        const op = (await this.leerCola())[0];
        if (!op) break;

        this.idEnCurso = op.id;
        try {
          console.log(`🚀 Sincronizando ${op.tipo} (${op.id})...`);
          await this.ejecutar(op);
          await this.modificarCola(cola => cola.filter(o => o.id !== op.id));
          console.log(`✅ ${op.tipo} sincronizada.`);
        } catch (error) {
          const tipoError = this.clasificarError(error);
          const mensaje = this.mensajeError(error);
          console.error(`❌ Error al sincronizar ${op.tipo} (${tipoError}):`, error);

          if (tipoError === 'auth') break; // token vencido: esperar a que el usuario vuelva a iniciar sesión

          const intentos = op.intentos + 1;
          if (tipoError === 'permanente' || intentos >= MAX_INTENTOS) {
            await this.moverAFallidas(op, mensaje);
            continue;
          }

          await this.modificarCola(cola =>
            cola.map(o => o.id === op.id ? { ...o, intentos, ultimoError: mensaje } : o)
          );
          break; // error de red / servidor: respetar el orden y reintentar más tarde
        }
      }
    } finally {
      this.idEnCurso = null;
      this.sincronizando = false;
      await this.actualizarContadores();
    }
  }

  /**
   * Aplica sobre datos locales (caché) los cambios que aún esperan sincronizarse,
   * para que una orden/visita modificada offline no se muestre con su estado viejo.
   */
  async aplicarPendientes(entidad: 'orden' | 'visita', id: string, datos: any): Promise<any> {
    if (!datos) return datos;
    const resultado = { ...datos };

    for (const op of await this.leerCola()) {
      const p = op.payload;
      if (entidad === 'orden' && p.idOrden === id) {
        if (op.tipo === 'estado_orden') resultado.estado = p.nuevoEstado;
        if (op.tipo === 'levantamiento' && p.requiereValidacionPR) resultado.estado = 'requiere_validacion_pr';
        if (op.tipo === 'cierre_orden') {
          resultado.estado = 'terminada';
          resultado.diagnostico = p.reporteFinal;
          if (p.firmaBase64) resultado.firma_base64 = p.firmaBase64;
        }
      }
      if (entidad === 'visita' && p.idVisita === id) {
        if (op.tipo === 'visita_accion') Object.assign(resultado, p.data);
        if (op.tipo === 'reporte_visita') Object.assign(resultado, p.payload);
      }
    }
    return resultado;
  }

  async obtenerFallidas(): Promise<OperacionPendiente[]> {
    return await this.storage.get(KEY_FALLIDAS) || [];
  }

  /** Devuelve las operaciones fallidas a la cola para reintentarlas. */
  async reintentarFallidas() {
    await this.conCandado(async () => {
      const fallidas: OperacionPendiente[] = await this.storage.get(KEY_FALLIDAS) || [];
      const cola = await this.leerCola();
      await this.storage.set(KEY_COLA, [...fallidas.map(op => ({ ...op, intentos: 0 })), ...cola]);
      await this.storage.set(KEY_FALLIDAS, []);
    });
    await this.actualizarContadores();
    this.sincronizar();
  }

  // ==========================================
  // EJECUCIÓN DE CADA TIPO DE OPERACIÓN
  // ==========================================

  private async ejecutar(op: OperacionPendiente) {
    const p = op.payload;
    const paso = (nombre: string, fn: () => Promise<unknown>) => this.paso(op, nombre, fn);

    switch (op.tipo) {
      case 'estado_orden':
        await firstValueFrom(this.odooSync.actualizarEstadoOrden(p.idOrden, p.nuevoEstado));
        break;

      case 'cantidades':
        for (const mat of p.materiales || []) {
          const idMat = mat.documentId || mat.id;
          await paso(`mat:${idMat}`, () => firstValueFrom(this.odooSync.actualizarMaterialOrden(idMat, {
            cantidad_requerida: mat.cantidad_usada,
            cantidad_usada: mat.cantidad_usada
          })));
        }
        break;

      case 'progreso':
        for (const mat of p.materiales || []) {
          const idMat = mat.documentId || mat.id;
          await paso(`mat:${idMat}`, () =>
            firstValueFrom(this.odooSync.actualizarMaterialOrden(idMat, { cantidad_usada: mat.cantidad_usada || 0 })));
        }
        await paso('visita', () => firstValueFrom(this.odooSync.crearVisita({
          tipo_visita: p.tipo_visita,
          fecha_inicio: p.creadoEn || op.creadaEn,
          fecha_fin: p.creadoEn || op.creadaEn,
          reporte_final: p.reporteFinal,
          orden_operativa: p.idOrden
        })));
        break;

      case 'levantamiento':
        for (const [i, producto] of (p.faltantes || []).entries()) {
          await paso(`material:${i}`, () => firstValueFrom(this.odooSync.crearMaterial({
            nombre_producto: producto.nombre.trim(),
            cantidad_requerida: producto.cantidad,
            cantidad_usada: 0,
            estado: producto.esFerreteria ? 'comprado_externo' : 'solicitado',
            precio_unitario: producto.precio,
            orden_operativa: { connect: [p.idOrden] }
          })));
        }
        await paso('visita', () => firstValueFrom(this.odooSync.crearVisita({
          tipo_visita: 'visita_tecnica',
          fecha_inicio: op.creadaEn,
          fecha_fin: op.creadaEn,
          reporte_final: `[LEVANTAMIENTO OFFLINE]: ${p.observaciones}`,
          orden_operativa: { connect: [p.idOrden] }
        })));
        if (p.requiereValidacionPR) {
          await paso('estado', () =>
            firstValueFrom(this.odooSync.actualizarOrden(p.idOrden, { estado: 'requiere_validacion_pr' })));
        }
        break;

      case 'recobro':
        await firstValueFrom(this.odooSync.crearRecobro(p));
        break;

      case 'cierre_orden': {
        for (const mat of p.materialesUsados || []) {
          const idMat = mat.documentId || mat.id;
          try {
            await paso(`mat:${idMat}`, () => firstValueFrom(this.odooSync.actualizarMaterialOrden(idMat, {
              cantidad_usada: mat.cantidad_usada || 0,
              estado: 'instalado'
            })));
          } catch (matError) {
            // Un material ya borrado en el servidor no debe bloquear el cierre de la orden
            if (this.clasificarError(matError) !== 'permanente') throw matError;
            console.warn(`Material ${idMat} rechazado por el servidor, se omite.`, matError);
          }
        }
        const fecha = p.fechaCierre || op.creadaEn;
        await paso('visita', () => firstValueFrom(this.odooSync.crearVisita({
          tipo_visita: 'instalacion',
          fecha_inicio: fecha,
          fecha_fin: fecha,
          reporte_final: p.reporteFinal,
          orden_operativa: p.idOrden
        })));
        await paso('cierre', () => firstValueFrom(this.odooSync.actualizarOrden(p.idOrden, {
          estado: 'terminada',
          diagnostico: p.reporteFinal
        })));
        if (p.fotoBase64) {
          await paso('foto', () => firstValueFrom(this.odooSync.subirArchivoBase64(
            p.fotoBase64, `evidencia_${p.idOrden}.jpg`, p.idOrden,
            'api::orden-operativa.orden-operativa', 'foto_evidencia'
          )));
        }
        if (p.firmaBase64) {
          await paso('firma', () => firstValueFrom(this.odooSync.subirArchivoBase64(
            p.firmaBase64, `firma_${p.idOrden}.png`, p.idOrden,
            'api::orden-operativa.orden-operativa', 'firma_cliente'
          )));
        }
        break;
      }

      case 'visita_accion': {
        const url = `${environment.apiUrl}/visita-cotizacions/${p.idVisita}`;
        if (p.tipo === 'cancelar') {
          try {
            await firstValueFrom(this.http.delete(url, { headers: this.headers(), body: { data: p.data } }));
          } catch (error) {
            // Si ya no existe, la cancelación ya está aplicada
            if (!(error instanceof HttpErrorResponse && error.status === 404)) throw error;
          }
        } else {
          await firstValueFrom(this.http.put(url, { data: p.data }, { headers: this.headers() }));
        }
        break;
      }

      case 'reporte_visita':
        await firstValueFrom(this.http.put(
          `${environment.apiUrl}/visita-cotizacions/${p.idVisita}`,
          { data: p.payload },
          { headers: this.headers() }
        ));
        break;
    }
  }

  private async paso(op: OperacionPendiente, nombre: string, fn: () => Promise<unknown>) {
    if (op.pasosHechos.includes(nombre)) return;
    await fn();
    op.pasosHechos.push(nombre);
    await this.modificarCola(cola =>
      cola.map(o => o.id === op.id ? { ...o, pasosHechos: [...op.pasosHechos] } : o)
    );
  }

  // ==========================================
  // UTILIDADES
  // ==========================================

  private headers() {
    return new HttpHeaders().set('Authorization', `Bearer ${localStorage.getItem('token')}`);
  }

  private clasificarError(error: unknown): 'red' | 'auth' | 'permanente' {
    if (!(error instanceof HttpErrorResponse)) return 'red';
    if (error.status === 0 || error.status >= 500 || error.status === 408 || error.status === 429) return 'red';
    if (error.status === 401 || error.status === 403) return 'auth';
    return 'permanente';
  }

  private mensajeError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      return `${error.status} ${error.error?.error?.message || error.message}`;
    }
    return String((error as any)?.message || error);
  }

  private async moverAFallidas(op: OperacionPendiente, mensaje: string) {
    await this.conCandado(async () => {
      const cola = await this.leerCola();
      const fallidas: OperacionPendiente[] = await this.storage.get(KEY_FALLIDAS) || [];
      fallidas.push({ ...op, intentos: op.intentos + 1, ultimoError: mensaje });
      await this.storage.set(KEY_FALLIDAS, fallidas);
      await this.storage.set(KEY_COLA, cola.filter(o => o.id !== op.id));
    });
  }

  private async leerCola(): Promise<OperacionPendiente[]> {
    return await this.storage.get(KEY_COLA) || [];
  }

  private modificarCola(fn: (cola: OperacionPendiente[]) => OperacionPendiente[]) {
    return this.conCandado(async () => {
      await this.storage.set(KEY_COLA, fn(await this.leerCola()));
    });
  }

  private conCandado<T>(fn: () => Promise<T>): Promise<T> {
    const resultado = this.candado.then(fn, fn);
    this.candado = resultado.catch(() => undefined);
    return resultado;
  }

  private async actualizarContadores() {
    this.pendientes$.next((await this.leerCola()).length);
    this.fallidas$.next((await this.obtenerFallidas()).length);
  }

  private async migrarColasLegadas() {
    await this.conCandado(async () => {
      const cola = await this.leerCola();
      for (const { key, tipo, clave } of COLAS_LEGADAS) {
        const items: any[] = await this.storage.get(key) || [];
        for (const payload of items) {
          cola.push({
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            tipo,
            payload,
            clave: clave?.(payload),
            creadaEn: payload.fechaCierre || new Date().toISOString(),
            intentos: 0,
            pasosHechos: []
          });
        }
        if (items.length) console.log(`📦 Migradas ${items.length} operaciones de '${key}'.`);
        await this.storage.remove(key);
      }
      await this.storage.set(KEY_COLA, cola);
    });
  }
}
